import { paletteGradient, palettePattern, paletteRoles, type Palette } from "./palette.js";
import type { Background } from "./scene.js";

/**
 * A palette, as the background a given style would draw from it.
 *
 * Its own module rather than a method on either side: it needs both the palette
 * maths and the background union, and the union already imports the mesh
 * layouts from the palette side.
 */
export function paletteBackground(kind: Background["kind"], palette: Palette): Background {
  if (kind === "mesh") {
    const { ground, fields } = paletteRoles(palette);
    return { kind: "mesh", layout: "drift", ground, fields: [...fields] };
  }
  if (kind === "gradient") {
    const { from, to } = paletteGradient(palette);
    return { kind: "gradient", from, to, angle: 160 };
  }
  if (kind === "pattern") {
    const { from, to } = palettePattern(palette);
    return { kind: "pattern", pattern: "linear", from, to };
  }
  return { kind: "color", color: paletteRoles(palette).ground };
}

/**
 * The colours a background is actually wearing.
 *
 * What the picker draws on a palette's strip: a style that takes two of four
 * shows two, because a four-swatch strip beside a two-colour gradient promises
 * two colours it will never draw. An image background wears none of its own.
 */
export function backgroundColours(background: Background): string[] {
  if (background.kind === "color") return [background.color];
  if (background.kind === "gradient" || background.kind === "pattern") return [background.from, background.to];
  if (background.kind === "mesh") return [background.ground, ...background.fields];
  return [];
}

/** Whether two backgrounds would draw the same thing. */
export function sameBackground(a: Background, b: Background): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "mesh" && b.kind === "mesh") {
    return (
      a.layout === b.layout &&
      a.ground === b.ground &&
      a.fields.length === b.fields.length &&
      a.fields.every((colour, index) => colour === b.fields[index])
    );
  }
  const worn = backgroundColours(a);
  const other = backgroundColours(b);
  return worn.length === other.length && worn.every((colour, index) => colour === other[index]);
}

/**
 * Which palette a background came from, if any.
 *
 * Matched on what it draws rather than on a stored id: an id would be a second
 * source of truth that goes stale the moment one colour is edited by hand, and
 * the panel would keep claiming a palette the card no longer wears.
 *
 * Compared against what each palette *would* produce for this style, not merely
 * "are these colours in that palette" — with a gradient wearing two, several
 * palettes can hold both and only one of them would have drawn it.
 */
export function paletteBehind(background: Background, palettes: readonly Palette[]): Palette | undefined {
  return palettes.find((palette) => sameBackground(paletteBackground(background.kind, palette), background));
}
